import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextareaAutosize,
  Divider,
} from "@mui/material";
import "./Modal.module.scss";
import GridTable from "cvmgmt-cube-grid-table";
import { IconButton, Toggle } from "cprime-foundational-components";
import Button from "../Common/CprimeButton";
import CloseIcon from "@mui/icons-material/Close";
import { getFeaturesRestrictionData } from "../../services/RequestAccess/RequestAccess";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
export interface ModalState {
  headLabel: string;
  isOpen: boolean;
  activateOn:
    | "rowselectApprove"
    | "rowselectRevoke"
    | "approve"
    | "deny"
    | "revoke"
    | "noAction";
  payload: Array<{
    access_id: string;
    subrequestId: string;
    roleIds: Array<string | number>;
  }>;
  isSyncSourceUserChangeProcess?: boolean;
}

interface ConfirmModalProps {
  modalState: ModalState;
  handleClose: () => void;
  handleDeny?: (roleData: any, comments: string, action?: string) => any;
  handleApprove?: (
    roleData: any,
    additional: boolean,
    comments: string,
    featureData: any,
  ) => any;
  actionLoading: boolean;
}

const FeatureRestrictionModal: React.FC<ConfirmModalProps> = React.memo(
  ({
    modalState,
    handleClose,
    handleDeny = () => {},
    handleApprove = () => {},
    actionLoading,
  }) => {
    const [gridData, setGridData] = useState<GridTableDataType | any>();
    const [toggleValue, setToggle] = useState<boolean>(false);
    const [comments, setComments] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(false);

    const hasNoRoles = useMemo(
      () => modalState?.payload?.every((d) => d?.roleIds?.length <= 0),
      [modalState?.payload],
    );

    const actionType = useMemo(() => {
      if (
        modalState?.activateOn === "rowselectRevoke" ||
        modalState?.activateOn === "revoke"
      ) {
        return "revoke";
      } else if (
        modalState?.activateOn === "deny" ||
        hasNoRoles ||
        modalState?.activateOn === "noAction"
      ) {
        return "deny";
      }
      return "approve";
    }, [modalState, hasNoRoles]);

    const fetchData = useCallback(async () => {
      try {
        setIsLoading(true);
        const result = await getFeaturesRestrictionData({
          action: actionType,
          data: modalState?.payload?.map((d) => ({
            subrequestId: d?.subrequestId,
            roleIds: Array?.isArray(d?.roleIds)
              ? d?.roleIds?.map(String)
              : [String(d?.roleIds)],
          })),
        });
        // Update column sizes for feature and permission names
        if (result?.data?.columns && Array.isArray(result.data.columns)) {
          result.data.columns = result.data.columns.map((col: any) => {
            if (col.id === "featurename") {
              return { ...col, size: 450 };
            }
            if (col.id === "permissionname") {
              return { ...col, size: 150 };
            }
            return col;
          });
        }
        setGridData(result?.data);
        setIsSubmitDisable(result?.data?.[0]?.roleIds?.length <= 0);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    }, [actionType, modalState?.payload]);

    useEffect(() => {
      fetchData();
    }, [modalState]);

    const renderCommentPlaceholder = useMemo(() => {
      if (modalState?.isSyncSourceUserChangeProcess) {
        return "Comments(Required for Revoke)";
      } else if (modalState?.activateOn === "rowselectApprove") {
        return "Comments(Required for Deny)";
      } else if (modalState?.activateOn === "approve") {
        return "Comments(Optional)";
      }
      return "Comments";
    }, [modalState?.activateOn, modalState?.isSyncSourceUserChangeProcess]);

    const handleCommentChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e?.target?.value?.length <= 500) {
          setComments(e?.target?.value);
        }
      },
      [],
    );

    return (
      <Dialog
        open={modalState?.isOpen}
        // onClose={handleClose}
        PaperProps={{
          style: {
            width: "1500px",
            height: "max-content",
            maxWidth: "1500px", // ensure it doesn't get constrained by default max width
          },
        }}
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "600",
          }}
        >
          <span>{modalState?.headLabel}</span>{" "}
          <IconButton onClick={() => handleClose()} data-testid="closemodal">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: "0" }}>
          <div style={{ padding: "0 24px 16px 24px" }}>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "400",
                color: "#000000",
                marginBottom: "16px",
                display: modalState?.activateOn === "noAction" ? "none" : "",
              }}
            >
              {["deny", "rowselectRevoke", "revoke", "noAction"]?.includes(
                modalState?.activateOn,
              )
                ? `For the Access Requests below, please provide a short reason for ${modalState?.activateOn === "revoke" || modalState?.activateOn === "rowselectRevoke" ? "revoking" : "denying"} the features requested below.`
                : `For the Access Requests below, the previously selected roles will approve the features requested.${!modalState?.isSyncSourceUserChangeProcess ? " Toggle to also approve additional features for all selected roles." : ""}`}
            </div>
            {!modalState?.isSyncSourceUserChangeProcess && (
              <div
                style={{
                  display:
                    ["deny", "rowselectRevoke", "revoke", "noAction"]?.includes(
                      modalState?.activateOn,
                    ) || hasNoRoles
                      ? "none"
                      : "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Toggle
                  size="large"
                  onChange={() => setToggle(!toggleValue)}
                  checked={toggleValue}
                  sx={{
                    overflow: "visible",
                    marginBottom: "4px",
                    "& .MuiSwitch-thumb": {
                      marginTop: "4px",
                    },
                  }}
                />
                <div>Restrict to requested features</div>
              </div>
            )}

            <GridTable
              title={
                modalState?.isSyncSourceUserChangeProcess
                  ? "Exisiting user feature access"
                  : modalState?.activateOn === "noAction"
                    ? "Details of the feature requested"
                    : "Additional Features for Eligible Roles"
              }
              // style={{ height: "20vh" }}
              data={gridData}
              isCustomizeColumns={false}
              isFiltering={false}
              isSorting={false}
              isPagination={false}
              loading={isLoading}
            />
            <div
              style={{
                width: "100%",
                display:
                  modalState?.activateOn === "noAction" ? "none" : "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
                marginTop: "32px",
                padding: "8px",
              }}
            >
              <div>{renderCommentPlaceholder}</div>
              <div>{comments?.length}/500</div>
            </div>
            <TextareaAutosize
              minRows={3}
              style={{
                borderRadius: "5px",
                width: "100%",
                display: modalState?.activateOn === "noAction" ? "none" : "",
              }}
              onChange={handleCommentChange}
              value={comments}
            />
          </div>
          <Divider />
          <div
            style={{
              padding: "16px 24px",
              display: modalState?.activateOn === "noAction" ? "none" : "flex",
              justifyContent:
                modalState?.activateOn === "rowselectApprove" ||
                modalState?.activateOn === "rowselectRevoke"
                  ? "space-between"
                  : "flex-end",
            }}
          >
            <Button onClick={handleClose} variant="tertiary">
              Cancel
            </Button>
            <div>
              {modalState?.isSyncSourceUserChangeProcess ? (
                <>
                  {/* Row click - show Approve and Revoke */}
                  {(modalState?.activateOn === "rowselectApprove" ||
                    modalState?.activateOn === "rowselectRevoke") && (
                    <>
                      <Button
                        sx={{
                          marginLeft: "5px",
                        }}
                        onClick={() =>
                          handleDeny(modalState?.payload, comments, "revoke")
                        }
                        disabled={comments?.length <= 0 || isLoading}
                        variant="danger"
                      >
                        Revoke
                      </Button>
                      <Button
                        onClick={() =>
                          handleApprove(
                            modalState?.payload,
                            toggleValue,
                            comments,
                            gridData,
                          )
                        }
                        disabled={
                          hasNoRoles ||
                          isLoading ||
                          isSubmitDisable ||
                          actionLoading
                        }
                        variant="primary"
                        sx={{
                          marginLeft: "5px",
                        }}
                      >
                        Approve
                      </Button>
                    </>
                  )}

                  {/* Click Approve - show only Approve */}
                  {modalState?.activateOn === "approve" && (
                    <Button
                      onClick={() =>
                        handleApprove(
                          modalState?.payload,
                          toggleValue,
                          comments,
                          gridData,
                        )
                      }
                      disabled={
                        hasNoRoles ||
                        isLoading ||
                        isSubmitDisable ||
                        actionLoading
                      }
                      variant="primary"
                      sx={{
                        marginLeft: "5px",
                      }}
                    >
                      Approve
                    </Button>
                  )}

                  {/* Click Revoke - show only Revoke */}
                  {modalState?.activateOn === "revoke" && (
                    <Button
                      sx={{
                        marginLeft: "5px",
                      }}
                      onClick={() =>
                        handleDeny(modalState?.payload, comments, "revoke")
                      }
                      disabled={comments?.length <= 0 || isLoading}
                      variant="danger"
                    >
                      Revoke
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    sx={{
                      display:
                        modalState?.activateOn === "approve" ? "none" : "",
                      marginLeft: "5px",
                    }}
                    onClick={() =>
                      handleDeny(
                        modalState?.payload,
                        comments,
                        modalState?.activateOn,
                      )
                    }
                    disabled={comments?.length <= 0 || isLoading}
                    variant="danger"
                  >
                    {modalState?.activateOn === "revoke" ||
                    modalState?.activateOn === "rowselectRevoke"
                      ? "Revoke"
                      : "Deny"}{" "}
                  </Button>

                  <Button
                    onClick={() =>
                      handleApprove(
                        modalState?.payload,
                        toggleValue,
                        comments,
                        gridData,
                      )
                    }
                    disabled={
                      hasNoRoles ||
                      isLoading ||
                      isSubmitDisable ||
                      actionLoading
                    }
                    variant="primary"
                    sx={{
                      marginLeft: "5px",
                      display:
                        modalState?.activateOn === "rowselectApprove" ||
                        modalState?.activateOn === "approve"
                          ? ""
                          : "none",
                    }}
                  >
                    Approve
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);

export default FeatureRestrictionModal;
